import { useCallback, useEffect } from 'react';
import {
  Box,
  Button,
  ScrollPane,
  SegmentedControl,
} from '@nimbus-ds/components';
import { ChevronLeftIcon, ChevronRightIcon } from '@nimbus-ds/icons';
import { useResponsive } from 'commons/components';
import { useToastStatus } from 'commons/hooks';
import {
  defaultFilters,
  FiltersParamsType,
  FiltersType,
} from 'domains/Orders/Orders/ordersService/constants';
import { SavedSearchInterface } from 'domains/Orders/Orders/ordersSlice/savedSearches';
import { trackingSearchBySavedSearches } from 'domains/Orders/tracking';
import useTranslationOrders from 'domains/Orders/useTranslationOrders';
import { SavedSearch } from './SavedSearch/SavedSearch';
import { useSavedSearches } from './useSavedSearches';
import { useSelectedSavedSearch } from './useSelectedSavedSearch';
import './SavedSearches.scss';

interface SavedSearchesProperties {
  appliedFilter: FiltersParamsType;
  onSavedSearchClick: (newFilters: FiltersParamsType) => void;
  visible: boolean;
}

export function SavedSearches({
  appliedFilter,
  onSavedSearchClick,
  visible,
}: Readonly<SavedSearchesProperties>) {
  const t = useTranslationOrders('savedSearches');
  const { isMobile } = useResponsive();
  const {
    savedSearches,
    savedSearchesStatus,
    fetchSavedSearches,
    createSavedSearchStatus,
    configSavedSearchesStatus,
  } = useSavedSearches();
  const { selectedSavedSearch, setSelectedSavedSearch } =
    useSelectedSavedSearch(appliedFilter);
  const { savedSearchesCount, cleanSavedSearchStatus } = useSavedSearches();

  useEffect(() => {
    if (savedSearchesStatus === 'idle' && !savedSearches.length) {
      fetchSavedSearches();
    }
  }, [savedSearches, fetchSavedSearches, savedSearchesStatus]);

  const onClick = useCallback(
    (savedSearch: SavedSearchInterface) => {
      // Saved search is already selected, so it should be deselected
      if (selectedSavedSearch?.id === savedSearch.id) {
        onSavedSearchClick(defaultFilters);
        setSelectedSavedSearch(null);
        trackingSearchBySavedSearches(
          savedSearch.key,
          !savedSearch.isDefault,
          false,
        );
      } else {
        const transformedFilters: FiltersType = {
          ...defaultFilters,
          ...savedSearch.filters,
        };
        onSavedSearchClick(transformedFilters);
        setSelectedSavedSearch(savedSearch);
        trackingSearchBySavedSearches(
          savedSearch.key,
          !savedSearch.isDefault,
          true,
        );
      }
    },
    [onSavedSearchClick, selectedSavedSearch, setSelectedSavedSearch],
  );

  useToastStatus({
    status: createSavedSearchStatus,
    success: t('toast.saveCreateSuccess'),
    error: t('toast.saveError'),
    onSuccess: cleanSavedSearchStatus,
    onError: cleanSavedSearchStatus,
  });

  useToastStatus({
    status: configSavedSearchesStatus,
    success: t('toast.saveConfigSuccess'),
    error: t('toast.saveError'),
    onSuccess: cleanSavedSearchStatus,
    onError: cleanSavedSearchStatus,
  });

  if (!savedSearches.length || savedSearchesStatus === 'loading') {
    return <Button.Skeleton width="300px" />;
  }

  return (
    <Box
      display={visible ? 'flex' : 'none'}
      flexDirection="row"
      alignItems="center"
      gap="0-5"
    >
      <div className="stratus--orders-saved-searches-scroll-pane">
        <ScrollPane
          display="grid"
          contentContainerProps={{
            flexShrink: '0',
          }}
          direction="horizontal"
          scrollPaneArrowEnd={
            <ScrollPane.ArrowHorizontalEnd>
              <div className="stratus--orders-saved-searches-arrow">
                <Button>
                  <ChevronRightIcon size="small" />
                </Button>
              </div>
            </ScrollPane.ArrowHorizontalEnd>
          }
          scrollPaneArrowStart={
            <ScrollPane.ArrowHorizontalStart>
              <div className="stratus--orders-saved-searches-arrow">
                <Button>
                  <ChevronLeftIcon size="small" />
                </Button>
              </div>
            </ScrollPane.ArrowHorizontalStart>
          }
          showGradients={false}
          scrollToItemOnClick
          showArrows={!isMobile}
          showScrollbar={false}
        >
          <SegmentedControl selectedSegments={[selectedSavedSearch?.id ?? '']}>
            {savedSearches
              .filter((ss) => !ss.hidden)
              .map((savedSearch) => (
                <SavedSearch
                  key={savedSearch.id}
                  isSelected={savedSearch.id === selectedSavedSearch?.id}
                  savedSearch={savedSearch}
                  count={savedSearchesCount?.[savedSearch.key]?.count}
                  onSavedSearchClick={onClick}
                />
              ))}
          </SegmentedControl>
        </ScrollPane>
      </div>
    </Box>
  );
}
